import React, {useRef, useState} from 'react';
import {Stack, Typography} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import './Chip.scss';
import ArrIconButton from '../../../../../../common/arr-icon-button/ArrIconButton';
import {Criteria} from '../../../../../types/Criteria';
import ChipEditor from './chip-editor/ChipEditor';

export interface ChipProps {
  criteria: Criteria;
  onDelete?: (criteria: Criteria) => void;
  onEdit?: (criteria: Criteria) => void;
  dataTestId: string;
  chipFocusRef?: React.RefObject<HTMLElement | null>;
}

const Chip: React.FC<ChipProps> = ({
  criteria,
  onDelete,
  onEdit,
  dataTestId,
  chipFocusRef,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const chipRef = useRef<HTMLElement | null>(null);
  const shouldRestoreFocus = useRef(false);
  const chipClassName =
    criteria.type === 'Operand' ? 'operand-chip' : 'operator-chip';

  const getFocusTarget = () => {
    return chipRef.current ?? null;
  };

  const handlePanelClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      setAnchorEl(null);
    } else {
      shouldRestoreFocus.current = true;
      onEdit && onEdit(criteria);
      setAnchorEl(e.currentTarget);
      setOpen(true);
    }
  };

  const closePopper = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const onMenuClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleSave = (updated: Criteria) => {
    onEdit && onEdit(updated);
    closePopper();
  };

  const handleDeleteClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onDelete && onDelete(criteria);
  };

  return (
    <>
      <Stack
        direction={'row'}
        spacing={1}
        className={chipClassName}
        tabIndex={0}
        ref={chipRef as any}
        onFocus={(e) => {
          if (chipFocusRef) {
            chipFocusRef.current = e.currentTarget;
          }
        }}
        onClick={(e) => {
          if (chipFocusRef) {
            chipFocusRef.current = e.currentTarget;
          }
          handlePanelClick(e);
        }}
      >
        <Typography
          className="typo-text label3_size"
          data-testid={`chip-typography-${dataTestId ?? (criteria.value.replaceAll(' ', '-') || 'chip')}`}
        >
          {criteria.value}
        </Typography>
        <ArrIconButton
          icon={<ClearIcon />}
          ariaLabel=""
          onClick={handleDeleteClick}
          size="small"
          className="icon-clear"
          testId={`chip-clear-${dataTestId ?? (criteria.value.replaceAll(' ', '-') || 'chip')}`}
        />
      </Stack>
      <ChipEditor
        anchorEl={anchorEl}
        open={open}
        criteria={criteria}
        onSave={handleSave}
        onClose={closePopper}
        onMenuClose={onMenuClose}
        anchorReturnRef={{current: getFocusTarget()} as any}
        restoreFocus={shouldRestoreFocus.current}
      />
    </>
  );
};

export default Chip;
